import { Link } from 'next-view-transitions';
import type { ButtonHTMLAttributes, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { ArrowLeft, ChevronRight, type LucideIcon } from 'lucide-react';

import { cn } from '@/components/ui/utils';

/**
 * The Chow mobile page frame, ported to the web guest surface.
 *
 * `.chow-root` supplies the zinc + forest tokens and the Fraunces/Inter pairing;
 * `.guest-v2` supplies the 16/32/40 gutters, 64px top bar and grouped rows from
 * the app. Keeping the frame here means the branch hub, place profile, account,
 * activity and task pages cannot drift apart again.
 */
export function GuestV2Page({
  children,
  className,
  topBar,
}: {
  children: ReactNode;
  className?: string;
  topBar?: ReactNode;
}) {
  return (
    <div data-experience="v2" className={cn('chow-root guest-v2', className)}>
      {topBar}
      {children}
    </div>
  );
}

export function GuestV2Container({
  children,
  reading = false,
  className,
}: {
  children: ReactNode;
  reading?: boolean;
  className?: string;
}) {
  return (
    <div className={cn('gv-container', reading && 'gv-reading', className)}>{children}</div>
  );
}

/**
 * 64px header: 44px circular back button, Fraunces title, trailing actions —
 * the same geometry as `components/Layout.tsx` in the mobile app. `overlay`
 * is for the place screen, where the bar floats on the hero photo.
 */
export function GuestV2TopBar({
  backHref,
  backLabel = 'Go back',
  title,
  subtitle,
  trailing,
  overlay = false,
  className,
}: {
  backHref?: string;
  backLabel?: string;
  title?: ReactNode;
  subtitle?: ReactNode;
  trailing?: ReactNode;
  overlay?: boolean;
  className?: string;
}) {
  return (
    <header className={cn('gv-topbar', overlay && 'gv-topbar--overlay', className)}>
      <GuestV2Container>
        <div className="gv-topbar__inner">
          {backHref ? (
            <Link href={backHref} aria-label={backLabel} className="gv-icon-btn">
              <ArrowLeft aria-hidden="true" className="size-5" />
            </Link>
          ) : null}
          {title ? (
            <div className="gv-topbar__title">
              {title}
              {subtitle ? <span className="gv-topbar__sub">{subtitle}</span> : null}
            </div>
          ) : (
            <div className="min-w-0 flex-1" />
          )}
          {trailing ? <div className="flex shrink-0 items-center gap-2">{trailing}</div> : null}
        </div>
      </GuestV2Container>
    </header>
  );
}

export function GuestV2Section({
  title,
  action,
  actionHref,
  children,
  className,
  id,
}: {
  title?: ReactNode;
  action?: ReactNode;
  actionHref?: string;
  children: ReactNode;
  className?: string;
  id?: string;
}) {
  return (
    <section className={cn('gv-section', className)} id={id}>
      {title || action || actionHref ? (
        <div className="gv-section__head">
          {title ? <h2 className="gv-section__title">{title}</h2> : <span className="flex-1" />}
          {actionHref ? (
            <Link href={actionHref} className="gv-section__action">
              {action ?? 'See all'}
            </Link>
          ) : action ? (
            <div className="shrink-0">{action}</div>
          ) : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/**
 * A Chow group: one 16px rounded card, hairline separators between 64px rows.
 * `rows` is intentionally children rather than an array so a caller can mix
 * links, buttons and statements without inventing a row schema.
 */
export function GuestV2Group({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('gv-group', className)}>{children}</div>;
}

export function GuestV2Row({
  icon: Icon,
  title,
  subtitle,
  href,
  trailing,
  tone = 'neutral',
  external = false,
  onClick,
  chevron,
  ariaExpanded,
  ariaControls,
  className,
}: {
  icon?: LucideIcon;
  title: ReactNode;
  subtitle?: ReactNode;
  href?: string;
  trailing?: ReactNode;
  tone?: 'neutral' | 'accent' | 'warning' | 'success';
  external?: boolean;
  onClick?: () => void;
  /** Show the row chevron. Defaults to true only when the row is a link. */
  chevron?: boolean;
  /** For rows that disclose a list in place (e.g. the language picker). */
  ariaExpanded?: boolean;
  ariaControls?: string;
  className?: string;
}) {
  const showChevron = chevron ?? Boolean(href);
  const content = (
    <>
      {Icon ? (
        <span className={cn('gv-row__icon', tone !== 'neutral' && `gv-row__icon--${tone}`)}>
          <Icon aria-hidden="true" className="size-[18px]" />
        </span>
      ) : null}
      <span className="gv-row__body">
        <span className="gv-row__title">{title}</span>
        {subtitle ? <span className="gv-row__sub">{subtitle}</span> : null}
      </span>
      {trailing ? <span className="gv-row__trailing">{trailing}</span> : null}
      {showChevron ? <ChevronRight aria-hidden="true" className="size-4 shrink-0 opacity-40" /> : null}
    </>
  );

  if (href && external) {
    return (
      <a href={href} className={cn('gv-row', className)} target="_blank" rel="noreferrer">
        {content}
      </a>
    );
  }
  if (href) {
    return (
      <Link href={href} className={cn('gv-row', className)}>
        {content}
      </Link>
    );
  }
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        className={cn('gv-row w-full text-left', className)}
      >
        {content}
      </button>
    );
  }
  return <div className={cn('gv-row gv-row--static', className)}>{content}</div>;
}

type GuestV2ButtonProps = {
  children: ReactNode;
  href?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'dark' | 'onbrand';
  size?: 'lg' | 'md' | 'sm';
  block?: boolean;
  className?: string;
  onClick?: () => void;
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
  disabled?: boolean;
  external?: boolean;
  ariaLabel?: string;
};

export function GuestV2Button({
  children,
  href,
  variant = 'primary',
  size = 'lg',
  block = false,
  className,
  onClick,
  type = 'button',
  disabled = false,
  external = false,
  ariaLabel,
}: GuestV2ButtonProps) {
  const classes = cn(
    'gv-btn',
    `gv-btn--${variant}`,
    size === 'md' && 'gv-btn--md',
    size === 'sm' && 'gv-btn--sm',
    block && 'gv-btn--block',
    className,
  );

  if (href) {
    if (external) {
      return (
        <a href={href} aria-label={ariaLabel} className={classes} target="_blank" rel="noreferrer">
          {children}
        </a>
      );
    }
    return (
      <Link href={href} aria-label={ariaLabel} className={classes}>
        {children}
      </Link>
    );
  }

  return (
    <button type={type} onClick={onClick} disabled={disabled} aria-label={ariaLabel} className={classes}>
      {children}
    </button>
  );
}

function handleSegmentedKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>) {
  const tablist = event.currentTarget.closest('[role="tablist"]');
  const tabs = Array.from(tablist?.querySelectorAll<HTMLButtonElement>(':scope > [role="tab"]') ?? []);
  const index = tabs.indexOf(event.currentTarget);
  if (index < 0 || tabs.length === 0) return;

  let nextIndex: number | null = null;
  switch (event.key) {
    case 'ArrowRight':
      nextIndex = (index + 1) % tabs.length;
      break;
    case 'ArrowLeft':
      nextIndex = (index - 1 + tabs.length) % tabs.length;
      break;
    case 'Home':
      nextIndex = 0;
      break;
    case 'End':
      nextIndex = tabs.length - 1;
      break;
    default:
      return;
  }

  event.preventDefault();
  const nextTab = tabs[nextIndex];
  nextTab?.focus();
  nextTab?.click();
}

export function GuestV2Segmented<T extends string>({
  options,
  value,
  onChange,
  label,
  counts,
  className,
}: {
  options: readonly { value: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
  label: string;
  counts?: Partial<Record<T, number>>;
  className?: string;
}) {
  return (
    <div role="tablist" aria-label={label} className={cn('gv-segmented', className)}>
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="tab"
          aria-selected={value === option.value}
          className="gv-segmented__btn"
          tabIndex={value === option.value ? 0 : -1}
          onKeyDown={handleSegmentedKeyDown}
          onClick={() => {
            if (value !== option.value) onChange(option.value);
          }}
        >
          {option.label}
          {counts?.[option.value] !== undefined ? (
            <span className="gv-segmented__count">{counts[option.value]}</span>
          ) : null}
        </button>
      ))}
    </div>
  );
}

export function GuestV2StatusPill({
  tone = 'neutral',
  children,
}: {
  tone?: 'neutral' | 'open' | 'closed' | 'accent' | 'warning';
  children: ReactNode;
}) {
  return (
    <span className={cn('gv-pill', tone !== 'neutral' && `gv-pill--${tone}`)}>
      {tone === 'open' || tone === 'closed' ? <span className="gv-dot" aria-hidden="true" /> : null}
      {children}
    </span>
  );
}
